import type WaRadioGroup from "@awesome.me/webawesome/dist/components/radio-group/radio-group.js";
import {
  focusedBodyOf,
  focusedForms,
  focusedValues,
} from "../content/lianxi/yufa-focused.js";
import type { FocusedAnswer } from "../fuxi/yufa-marking.js";
import { teachingOn } from "./lianxi-yufa-teaching.js";
import { meaningBodyOf, meaningText } from "../content/lianxi/yufa-meaning.js";
import { updateRepairInput } from "./lianxi-yufa-repair.js";

/**
 * The Web Awesome radio group a question still answers through: a visual
 * scene's sentences and a repair's parts. Every other question chosen from
 * options draws them as rows (`./lianxi-xuanze.ts`), which
 * {@link choiceHolder} finds.
 */
export function focusedGroup(form: HTMLFormElement): WaRadioGroup | null {
  return form.querySelector<WaRadioGroup>(
    "wa-radio-group[data-cb-yufa-focused], wa-radio-group[data-cb-yufa-meaning], wa-radio-group[data-cb-yufa-repair]",
  );
}

/** The fieldset of a contrast, placement or meaning question drawn as rows. */
export function choiceHolder(form: HTMLFormElement): HTMLElement | null {
  return form.querySelector<HTMLElement>("fieldset[data-cb-yufa-choices]");
}

/** What carries a contrast, placement or meaning question's key, either way it is drawn. */
function keyHolder(form: HTMLFormElement): HTMLElement | null {
  return choiceHolder(form) ?? focusedGroup(form);
}

/** The value chosen, from the rows or the radio group, or nothing. */
function chosenValue(
  form: HTMLFormElement,
): string | number | null | undefined {
  const holder = choiceHolder(form);

  return holder === null
    ? focusedGroup(form)?.value
    : (holder.querySelector<HTMLInputElement>("input[type=radio]:checked")
        ?.value ?? null);
}

/** The explicit empty option has a numeric value. An unset group has no answer. */
export function focusedAnswer(
  form: HTMLFormElement,
): FocusedAnswer | undefined {
  const group = keyHolder(form);
  const value = chosenValue(form);
  const meaning = meaningBodyOf(
    JSON.parse(group?.dataset["cbYufaMeaning"] ?? "null") as unknown,
  );
  if (meaning !== undefined) {
    if (value === undefined || value === null || value === "") {
      return undefined;
    }
    const chosen = Number(value);
    const teaching = teachingOn(form);
    return Number.isInteger(chosen) &&
      chosen >= 0 &&
      chosen < meaning.options.length
      ? {
          kind: meaning.kind,
          chosen,
          accepted: meaning.accepted,
          choices: meaning.options.map((option, value) => ({
            value,
            text: meaningText(option),
            language: option.language,
          })),
          ...(teaching === undefined ? {} : { teaching }),
        }
      : undefined;
  }
  const body = focusedBodyOf(
    JSON.parse(group?.dataset["cbYufaFocused"] ?? "null") as unknown,
  );
  if (
    value === undefined ||
    value === null ||
    value === "" ||
    body === undefined
  ) {
    return undefined;
  }
  const chosen = Number(value);
  const values = focusedValues(body);
  const teaching = teachingOn(form);
  return Number.isInteger(chosen) && values.includes(chosen)
    ? {
        kind: body.kind,
        chosen,
        accepted: body.accepted,
        choices: focusedForms(body).map((form, at) => ({
          value: values[at]!,
          text: form.simplified,
        })),
        ...(teaching === undefined ? {} : { teaching }),
      }
    : undefined;
}

/** Number shortcuts select the displayed boundary without submitting the question. */
export function pickFocused(form: HTMLFormElement, digit: number): void {
  const group = focusedGroup(form);
  const option = group?.querySelectorAll<HTMLElement>("wa-radio")[digit];
  if (group !== null && !group.disabled && option !== undefined) {
    group.value = option.getAttribute("value")!;
    updateRepairInput(form);
    option.focus();
  }
}
